import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Dialog, toast, type SegmentedTabItem } from '@/ui';
import { marketplaceKeys } from '@/lib/task-query-keys';
import { filtersToServerParams } from '@/lib/task-list-url';
import { useServerList } from '@/lib/task-server-list';
import { isCustomerOrgAdmin } from '@/auth/types';
import { useSessionStore } from '@/auth/session.store';
import { HowItWorks } from '@/features/help/HowItWorks';
import { usePhaseDurations } from '@/features/help/usePhaseDurations';
import { fetchTaskPage } from './api';
import { useTaskCounts, useUpdateTask } from './queries';
import {
  CUSTOMER_REQUEST_TABS as TABS,
  customerRequestTabParams as tabParams,
  type CustomerRequestTab as Tab,
} from './tab-params';
import { sameId, type TaskRow } from './types';
import { useUrlList } from './useUrlList';
import { CustomerRequestCard } from './components/CustomerRequestCard';
import { ListScreen } from './components/ListScreen';
import { PaymentDueBanner } from './components/PaymentDue';
import { taskErrorMessage } from './error-message';

/**
 * MUSTERININ KENDI TALEPLERI.
 *
 * Sekmeler mobil ile birebir ayni parametreleri uretiyor; `passedTasks` +
 * `taskStatus` ikilisi sunucudaki filtre sozlesmesi. Esleme ./tab-params.ts'te
 * (liste ve sekme rozeti AYNI fonksiyondan besleniyor):
 *   Acik Talepler : teklif/karar fazindaki 'new' talepler
 *   Islemde       : 'pending' + 'payment_received' (kabul edilmis, kapanmamis). Sekme anahtari
 *                   'approvedRequests' (URL `sekme` degeri degismedi); 2026-10-02'ye kadar
 *                   etiketi "Onaylanan"di.
 *   Gecmis        : kapali faz + kapali durum — aktif bir is iki sekmede birden GORUNMEZ
 *
 * TALEP MERKEZLI (2026-10-02, "Alternatif A"): ayri "Gelen teklifler" ekrani KALDIRILDI. Kart
 * fazina gore teklif sayisini + en iyi kuru ya da kazanani gosterir; teklifler, secim ve odeme
 * bilgileri talebin kendi sayfasinda (/musteri/talepler/:taskId). Eski ekranin tek ozgun isi —
 * org yoneticisine talep sahibinin adi — bu karta tasindi.
 *
 * Sahiplik parametresi `taskUserId`: satirdaki alan adi `userId` ama SORGU
 * parametresinin adi bu — ikisi ayni sey degil ve karistirilirsa filtre sessizce
 * uygulanmaz (liste TUM taleplerle doner).
 */

const DEFAULT_TAB: Tab = 'openRequests';

const isTab = (value: string): value is Tab => (TABS as readonly string[]).includes(value);

const SORT_OPTIONS = [
  { value: 'taskCreatedAt:desc', labelKey: 'filter.sortNewest' },
  { value: 'taskCreatedAt:asc', labelKey: 'filter.sortOldest' },
  { value: 'taskAmount:desc', labelKey: 'filter.sortAmountDesc' },
  { value: 'taskAmount:asc', labelKey: 'filter.sortAmountAsc' },
] as const;

export function CustomerRequestsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const snapshot = useSessionStore((state) => state.snapshot);
  const userId = snapshot?.user.id;
  // Musteri org yoneticisi org'un TUM taleplerini gorur; baskasinin kartinda sahibin adi yazar.
  const orgAdmin = isCustomerOrgAdmin(snapshot);
  const url = useUrlList(DEFAULT_TAB);
  const tab: Tab = isTab(url.tab) ? url.tab : DEFAULT_TAB;

  const [confirm, setConfirm] = useState<{ task: TaskRow; status: 'cancelled' | 'warning' } | null>(
    null,
  );
  const updateTask = useUpdateTask();
  // Tamamlanan isleme itiraz suresi SUNUCUDAN (/v1/config phases.disputeSeconds).
  const { disputeSeconds } = usePhaseDurations();

  const params = useMemo(
    () =>
      userId
        ? { taskUserId: userId, ...tabParams(tab), ...filtersToServerParams(url.filters) }
        : {},
    [userId, tab, url.filters],
  );

  const list = useServerList<TaskRow>({
    baseKey: marketplaceKeys.taskLists(),
    params,
    fetchPage: fetchTaskPage,
    offset: url.offset,
    onParamsChanged: url.resetOffset,
    enabled: Boolean(userId),
    // Buronun adimlari (odeme alindi, tamamlandi) web'e push ile gelmiyor; etkin sekmeler yoklanir.
    pollMs: tab === 'passedRequests' ? undefined : 15_000,
  });

  // Rozet sayilari. Acik sekme icin de cekiliyor: liste sayfalanmis geliyor, dolayisiyla
  // ekrandaki satir sayisi TOPLAM degil. Sayaclar liste ile AYNI sunucu sorgusundan
  // (countOnly) besleniyor, o yuzden ayrisamiyorlar. Urun karari (2026-09-23): rozet
  // kullanicinin FILTRELERINI yansitir — filtreler bu yuzden sayaca da gidiyor.
  const counts = useTaskCounts(
    TABS.map((value) => ({
      value,
      params: {
        taskUserId: userId ?? '',
        ...tabParams(value),
        ...filtersToServerParams(url.filters),
      },
    })),
    Boolean(userId),
  );

  // ODEMESI BEKLENEN onayli talepler (2026-09-26) — Acik Talepler'in ustundeki serit icin.
  // Musteri teklifi onaylayip bu sekmede kaliyor ve odemeyi unutuyordu; 15 dakika dolunca is
  // incelemeye aliniyor. Kapsam listeyle ayni (`taskUserId`) ama FILTRELER GITMIYOR: bir para
  // birimi ya da arama filtresi, yapilmasi gereken bir odemeyi gizlememeli. Yalniz bu sekmede
  // cekilir ve liste gibi yoklanir (buronun "Odeme alindi"si web'e push ile gelmiyor).
  const paymentDueCount = useTaskCounts(
    [{ value: 'paymentDue', params: { taskUserId: userId ?? '', taskStatus: 'pending' } }],
    Boolean(userId) && tab === 'openRequests',
    15_000,
  ).paymentDue;

  const tabs: SegmentedTabItem[] = [
    { value: 'openRequests', label: t('task.tabs.openRequests'), count: counts.openRequests },
    // Anahtar 'approvedRequests' (URL ve rozet sayaci ayni), etiket "Islemde" (2026-10-02).
    { value: 'approvedRequests', label: t('task.tabs.inProgress'), count: counts.approvedRequests },
    { value: 'passedRequests', label: t('task.tabs.past'), count: counts.passedRequests },
  ];

  const renderRow = useCallback(
    (task: TaskRow) => {
      // Sahiplik: yonetici org'un TUM taleplerini gorur ama BASKASININKINDE salt okunurdur
      // (kazanan secimi ve iptal talebi acan kisidedir; sunucu da postUpdateTask'i cagiranin
      // kendi talebine kelepceli tutuyor). Bilgi olarak sahip adi gosteriliyor.
      const isOwner = sameId(task.taskUserId, userId);
      return (
        <CustomerRequestCard
          task={task}
          receivedAtMs={list.receivedAtMs}
          disputeSeconds={disputeSeconds}
          isOwner={isOwner}
          ownerName={
            orgAdmin && !isOwner ? (task.userFullName ?? t('task.unknownOwner')) : undefined
          }
          onOpen={() => {
            void navigate(`/musteri/talepler/${task.id}`);
          }}
          onCancel={() => {
            setConfirm({ task, status: 'cancelled' });
          }}
          onWarn={() => {
            setConfirm({ task, status: 'warning' });
          }}
        />
      );
    },
    [list.receivedAtMs, navigate, userId, orgAdmin, disputeSeconds, t],
  );

  return (
    <>
      <ListScreen
        title={t('task.requestsTitle')}
        header={
          <>
            <HowItWorks audience="customer" />
            {tab === 'openRequests' && paymentDueCount !== undefined && paymentDueCount > 0 ? (
              <PaymentDueBanner
                count={paymentDueCount}
                onAction={() => {
                  url.setTab('approvedRequests');
                }}
              />
            ) : null}
          </>
        }
        tabs={tabs}
        tab={tab}
        onTabChange={url.setTab}
        filters={url.filters}
        onFiltersChange={url.setFilters}
        sortOptions={SORT_OPTIONS}
        list={list}
        onOffsetChange={url.setOffset}
        onClearFilters={() => {
          url.setFilters({ ...url.filters, q: '', currencies: [], createdFrom: '', createdTo: '', amountMin: '', amountMax: '' });
        }}
        rowKey={(task) => task.id}
        renderRow={renderRow}
      />

      <Dialog
        open={confirm !== null}
        title={confirm?.status === 'warning' ? t('task.warning') : t('task.areYouSure')}
        {...(confirm?.status === 'warning' ? { description: t('task.paymentNotMade') } : {})}
        confirmLabel={t('actions.confirm')}
        cancelLabel={t('actions.cancel')}
        tone={confirm?.status === 'cancelled' ? 'danger' : 'primary'}
        busy={updateTask.isPending}
        onCancel={() => {
          setConfirm(null);
        }}
        onConfirm={() => {
          if (!confirm) return;
          updateTask.mutate(
            { taskId: confirm.task.id, taskStatus: confirm.status },
            {
              onSuccess: () => {
                setConfirm(null);
                toast({ message: t('common.success'), tone: 'success' });
              },
              onError: (error) => {
                setConfirm(null);
                // Sunucu SEBEBI soyluyor (CANCEL_WINDOW_CLOSED, WARNING_TOO_EARLY...).
                toast({ message: taskErrorMessage(error, t), tone: 'danger' });
              },
            },
          );
        }}
      />
    </>
  );
}
